import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, Clock, FileText, Ban } from "lucide-react";
import { API } from "../lib/api";

/* Public view-only page for an expiring document share. */
export default function SharedDoc() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    fetch(`${API}/doc-shares/public/${token}`)
      .then(async (r) => {
        if (r.status === 410) throw new Error("revoked");
        if (!r.ok) throw new Error("notfound");
        setMeta(await r.json());
      })
      .catch((e) => setErr(e.message));
  }, [token]);

  const fileUrl = `${API}/doc-shares/public/${token}/file`;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="shared-doc-page">
      <div className="h-1.5 flex">{["#4285F4", "#EA4335", "#F9AB00", "#34A853"].map((c) => <div key={c} className="flex-1" style={{ background: c }} />)}</div>
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">TravelerPulse.pro · Secure document share</div>
        {err ? (
          <div className="bg-white border border-rose-200 p-8 text-center mt-4" data-testid="shared-doc-revoked">
            <Ban className="w-12 h-12 mx-auto text-rose-500 mb-3" />
            <div className="font-heading font-900 text-2xl text-rose-600">ACCESS REVOKED</div>
            <p className="text-sm text-muted-foreground mt-2">
              {err === "revoked" ? "This share has expired or was revoked by the owner. Ask them for a new link if you still need it."
                : "This link does not exist."}
            </p>
          </div>
        ) : !meta ? (
          <p className="text-sm text-muted-foreground mt-4">Loading…</p>
        ) : (
          <div className="bg-white border border-zinc-200 mt-4" data-testid="shared-doc-card">
            <div className="p-4 border-b border-zinc-100 flex flex-wrap items-center gap-2">
              <FileText className="w-5 h-5" style={{ color: "#4285F4" }} />
              <span className="font-heading font-800">{meta.label}</span>
              <span className="ml-auto inline-flex items-center gap-1 text-xs font-600 px-2 py-0.5 rounded-full" style={{ background: "#F9AB001A", color: "#B07C00" }} data-testid="shared-doc-expiry">
                <Clock className="w-3 h-3" /> Expires {new Date(meta.expires_at).toLocaleString()}
              </span>
            </div>
            {meta.note && <p className="px-4 pt-3 text-sm text-muted-foreground">{meta.note}</p>}
            <div className="p-4">
              {meta.content_type === "application/pdf"
                ? <iframe src={fileUrl} title={meta.label} className="w-full h-[70vh] border border-zinc-200" data-testid="shared-doc-pdf" />
                : <img src={fileUrl} alt={meta.label} className="max-w-full border border-zinc-200" data-testid="shared-doc-image" />}
            </div>
            <p className="px-4 pb-4 text-[11px] text-muted-foreground flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" style={{ color: "#34A853" }} />
              View-only, time-limited share. The owner can revoke access at any time; every view is logged.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
